import {Dispatch, SetStateAction} from 'react';
import {Socket} from 'socket.io-client';
import {
  ArchiveRoomRequest,
  ArchiveRoomResponse,
  BlockListResponse,
  blockRequest,
  BlockResponse,
  DeleteMessageRequest,
  DeleteMessageResponse,
  DeliveredRequest,
  DeliveredResponse,
  filterRequest,
  GetMessagesRequest,
  GetMessagesResponse,
  GetRoomsRequest,
  GetRoomsResponse,
  Message,
  ResponseError,
  Room,
  SeenRequest,
  SeenResponse,
  SendMessageRequest,
  SendMessageResponse,
  TypingRequest,
  UnblockRequest,
  UnBlockResponse,
  UnreadPostRoomsRequest,
  UnreadPostRoomsResponse,
  userInfoRequest,
  UserInfoResponse,
} from '../utils/types';
import {initialState, SocketAction, SocketState} from './socketReducer';
import type {ChatParticipant} from '../utils/chatParticipants';
export interface SocketContextProps {
  socket: Socket | null;
  connectionStatus: string;
  connectSocket: () => void;
  disconnectSocket: () => void;
  blockUser: (data: blockRequest) => Promise<BlockResponse | ResponseError>;
  unblockUser: (data: UnblockRequest) => Promise<UnBlockResponse | ResponseError>;
  getBlockList: () => Promise<BlockListResponse | ResponseError>;
  emitDelivered: (data: DeliveredRequest) => Promise<DeliveredResponse | ResponseError>;
  emitSeen: (data: SeenRequest) => Promise<SeenResponse | ResponseError>;
  emitTyping: (data: TypingRequest) => Promise<void>;
  emitArchiveRoom: (data: ArchiveRoomRequest) => Promise<ArchiveRoomResponse>;
  getRooms: (data: GetRoomsRequest) => Promise<GetRoomsResponse | ResponseError>;
  getSystemMessageRooms: (data: GetRoomsRequest) => Promise<GetRoomsResponse | ResponseError>;
  getMessages: (data: GetMessagesRequest) => Promise<GetMessagesResponse | ResponseError>;
  state: SocketState;
  sendMessage: (data: SendMessageRequest) => Promise<SendMessageResponse>;
  removeQueuedMessage: (room_id: string, message_id: number) => void;
  deleteMessage: (data: DeleteMessageRequest) => Promise<DeleteMessageResponse | Error>;
  selectRoom: (data: string) => void;
  dispatch: Dispatch<SocketAction>;
  addFailedMessageToQueue: (messageData: SendMessageRequest, room_id: string) => void;
  updateFailedMessageQueue: (messages: SendMessageRequest[], room_id: string) => void;
  addPendingMessageToQueue: (messageData: SendMessageRequest, room_id: string) => void;
  updatePendingMessageQueue: (messages: SendMessageRequest[], room_id: string) => void;
  fetchRoom: (listingId: number, sellerId: number, buyerId: number) => void;
  GetUnreadRoomsCount: () => any;
  GetUnreadPostRoomCount: (post_id: UnreadPostRoomsRequest) => any;
  getFilteredMessageRooms: (data: filterRequest) => Promise<GetRoomsResponse | ResponseError>;
  getUserInfo: (user_id: userInfoRequest) => Promise<UserInfoResponse>;
  setRoomListingInfo: (listingId: number) => void;
  setBuyerInfo:(memberId: number) =>void;
  selfParticipant: ChatParticipant | null;
  setFilterStatus: (filter: {
    filter_by: 'all' | 'buyer' | 'seller' | 'listing_id' | 'verified';
    unread_rooms: boolean;
    system_messages: boolean;
  }) => void;
  updateUnreadRoomsCount: (count: number) => void;
  setSelectedRoomDetails: (res: string) => void;
}
export const initialContext: SocketContextProps = {
  socket: null,
  connectionStatus: initialState.connectionStatus,
  connectSocket: () => {},
  disconnectSocket: () => {},
  blockUser: async (): Promise<BlockResponse> => ({
    message: '',
    success: false,
    data: { room_id: '', user_id: '' },
  }),
  unblockUser: async (): Promise<UnBlockResponse> => ({
    message: '',
    success: true,
  }),
  getBlockList: async (): Promise<BlockListResponse> => ({
    data: { items: [] },
    message: '',
    success: false,
  }),
  emitDelivered: async (): Promise<DeliveredResponse> => ({
    message: '',
    success: false,
  }),
  emitSeen: async (): Promise<SeenResponse> => ({
    message: '',
    success: false,
    message_id: 0,
    room_id: '',
  }),
  emitTyping: async () => {},
  emitArchiveRoom: async (): Promise<ArchiveRoomResponse> => ({
    message: '',
    success: false,
    rooms_ids: [],
  }),
  getRooms: async (): Promise<GetRoomsResponse> => ({
    success: true,
    message: '',
    latest_room_timestamp: null,
    rooms: [],
  }),
  getSystemMessageRooms: async (): Promise<GetRoomsResponse> => ({
    success: true,
    message: '',
    latest_room_timestamp: null,
    rooms: [],
  }),
  getMessages: async (): Promise<GetMessagesResponse> => ({
    success: true,
    message: '',
    has_more: false,
    messages: [],
    sync_timestamp: 0,
  }),
  state: initialState,
  GetUnreadRoomsCount: () => ({
    data: {
      success: false,
      rooms_unread_count: 0,
    },
  }),
  GetUnreadPostRoomCount: () => ({
    success: false,
    count_rooms: 0,
    post_id:0
  }),
  sendMessage: async (): Promise<SendMessageResponse> => ({
    message: '',
    success: false,
    data: {
      room: {
        room_id: '',
        seller: { id: 0, name: '', avatar: '' },
        buyer: { id: 0, name: '', avatar: '' },
        post: { id: 0, title: '', uri: '', country: '', type: '', cv_id: '' },
      },
      message: {
        id: 0,
        sender_id: 0,
        recipient_id: 0,
        sent_at: '',
        delivered_at: 0,
        seen_at: 0,
        sender_deleted_at: 0,
        recipient_deleted_at: 0,
        body: {
          text: '',
          icon: '',
          share_media: { size: 0, uri: '' },
          system_message: { icon: '', bgColor: '', fColor: '' },
        },
        type: 0,
        sub_type: 0,
      },
    },
  }),
  deleteMessage: async (): Promise<DeleteMessageResponse | Error> => ({
    message: '',
    success: false,
    data: { message_id: 0, room_id: '' },
  }),
  selectRoom: (data: string) => {},
  removeQueuedMessage: () => {},
  addFailedMessageToQueue: (messageData: SendMessageRequest, room_id: string) => {},
  updateFailedMessageQueue: (messages: SendMessageRequest[], room_id: string) => {},
  addPendingMessageToQueue: (messageData: SendMessageRequest, room_id: string) => {},
  updatePendingMessageQueue: (messages: SendMessageRequest[], room_id: string) => {},
  fetchRoom: (listingId: number, sellerId: number, buyerId: number) => {},
  getFilteredMessageRooms: async (): Promise<GetRoomsResponse> => ({
    success: true,
    message: '',
    latest_room_timestamp: null,
    rooms: [],
  }),
  getUserInfo: async ({ user_id }: userInfoRequest): Promise<UserInfoResponse> => ({
    uid: user_id,
    response_time: 0,
    name: '',
    avatar: '',
    resp_time_ver: 0,
    real_response_time: 0,
    answered_response_time: 0,
    res_rate: 0,
    avg_response_time: 0,
  }),
  setRoomListingInfo: (listingId: number) => {},
  setBuyerInfo:(memberId:number)=>{},
  selfParticipant: null,
  setFilterStatus: (filter) => {},
  updateUnreadRoomsCount: (count: number) => {},
  dispatch: (() => {}) as Dispatch<SocketAction>, // Temporary stub
  setSelectedRoomDetails: async () => {},
};
